// usbCaptureScope.js — which captured traffic a proxy-side rewrite can reach.
//
// IU Rewrite (and Chaos) act on requests IN FLIGHT through
// the MITM proxy. USB capture (captureSource 'logcat') only reads the phone's
// log after the app already sent the request straight to Google, so no rule
// can ever change it. These helpers let the UI say so instead of a rule that
// silently "doesn't work".

const isUsbRow = (r) => !!(r && r.captureSource === 'logcat');

function isAndroidRow(r, groupKey) {
  const s = `${(r && (r.platformClass || r.devicePlatform || r.device)) || ''} ${groupKey || ''}`;
  return /android/i.test(s);
}

/**
 * androidCaptureMix(groupedResults) -> { usb, proxied }
 * Counts Android rows by how they were captured.
 */
export function androidCaptureMix(groupedResults) {
  const out = { usb: 0, proxied: 0 };
  for (const [key, rows] of Object.entries(groupedResults || {})) {
    for (const r of Array.isArray(rows) ? rows : []) {
      if (!isAndroidRow(r, key)) continue;
      if (isUsbRow(r)) out.usb++; else out.proxied++;
    }
  }
  return out;
}

/** Device groups whose every captured row came from USB capture. */
export function usbOnlyDevices(groupedResults) {
  return Object.entries(groupedResults || {})
    .filter(([, rows]) => Array.isArray(rows) && rows.length > 0 && rows.every(isUsbRow))
    .map(([key]) => key);
}

/**
 * ruleTargetsAndroid(rule) -> bool
 * Scoped to an Android device, or (unscoped) matching an Android ad unit:
 * `droid` in the iu path is the house convention (droidmob/droidtab).
 */
export function ruleTargetsAndroid(rule) {
  if (!rule) return false;
  if (rule.device) return /android|droid/i.test(rule.device);
  return /droid|android/i.test(`${rule.match || ''} ${rule.key || ''}`);
}

/**
 * ruleUsbBlocked(rule, groupedResults) -> null | 'all' | 'some'
 * 'all'  — every request this rule could match was USB-captured: it can't fire.
 * 'some' — the session mixes USB and proxied Android traffic: it only
 *          fires on the proxied part.
 */
export function ruleUsbBlocked(rule, groupedResults) {
  if (!rule) return null;
  if (rule.device) {
    if (usbOnlyDevices(groupedResults).includes(rule.device)) return 'all';
    if (!ruleTargetsAndroid(rule)) return null;
  } else if (!ruleTargetsAndroid(rule)) {
    return null;
  }
  const { usb, proxied } = androidCaptureMix(groupedResults);
  if (!usb) return null;
  return proxied ? 'some' : 'all';
}
